import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Building2,
  MapPin,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ArrowLeft,
  Award,
  Users,
  School,
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';

import {
  ExpenditureBarChart,
  UtilizationGauge,
  TrendLineChart,
  RankingTable,
} from '../components/charts';
import { YearDropdown, PhaseDropdown } from '../components/filters';
import { useGetTopPerformingRoWiseQuery, useGetRegionHeadsBreakdownQuery } from '../services/api';

import type {
  RegionPerformance,
  SchoolPerformance,
  Head,
  MonthlyTrendData,
  HeadCode,
  PMShriPhase,
} from '../types';
import { DEFAULT_HEADS, HEAD_COLORS } from '../types';

type TabType = 'overview' | 'heads' | 'schools' | 'trends';

const RegionAnalyticsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roId } = useParams<{ roId: string }>();
  const carouselImages = [img1];

  // Get initial filters from location state if available
  const initialYear = location.state?.selectedYear || null;
  const initialPhase = location.state?.selectedPhase || null; // 'All' maps to null for API

  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedYear, setSelectedYear] = useState<number | null>(initialYear);
  const [selectedPhase, setSelectedPhase] = useState<number | null>(initialPhase);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'rank' | 'utilization' | 'sanctioned'>('rank');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // API Call
  const { data: apiResponse, isLoading, error } = useGetTopPerformingRoWiseQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    phase: selectedPhase || undefined,
    roid: parseInt(roId || '0'),
    limit: 1000,
  });

  const { data: headsBreakdownResponse } = useGetRegionHeadsBreakdownQuery({
    roid: parseInt(roId || '0'),
    year: selectedYear,
    phase: selectedPhase,
  });

  const [region, setRegion] = useState<(RegionPerformance & { schools: SchoolPerformance[]; trends: MonthlyTrendData[] }) | null>(null);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;
  };

  useEffect(() => {
    if (apiResponse?.data?.data && apiResponse.data.data.length > 0) {
      const roData = apiResponse.data.data[0];
      const apiSchools = roData.schools || [];

      // Map API Schools to SchoolPerformance type
      const mappedSchools: SchoolPerformance[] = apiSchools.map((s, idx) => ({
        kvId: s.kvID,
        kvCode: `KV${s.kvID}`, // No KV Code in this API, using ID as fallback
        schoolName: s.schoolName,
        kvName: s.schoolName,
        roId: roData.roID,
        roName: roData.roName,
        region: roData.roName,
        stateId: 0,
        stateName: '-',
        districtName: '-',
        pmShriStatus: 'active',
        pmShriPhase: 1, // Phase not in school object in this API
        totalSanctioned: s.sanctionedAmount,
        totalReceived: s.receivedAmount,
        totalSpent: s.spentAmount,
        totalUnspent: (s.receivedAmount - s.spentAmount),
        // Calculate utilization manually: (Spent / Received) * 100
        overallUtilization: s.receivedAmount > 0 ? (s.spentAmount / s.receivedAmount) * 100 : 0,
        utilizationPercentage: s.receivedAmount > 0 ? (s.spentAmount / s.receivedAmount) * 100 : 0,
        targetUnits: 0,
        completedUnits: 0,
        efficiencyPercentage: s.receivedAmount > 0 ? (s.spentAmount / s.receivedAmount) * 100 : 0,
        recurringAmount: s.recurringAmount || 0,
        recurringSpent: 0,
        recurringUtilization: 0,
        nonRecurringAmount: s.nonRecurringAmount || 0,
        nonRecurringSpent: 0,
        nonRecurringUtilization: 0,
        headWiseData: [],
        performanceScore: s.receivedAmount > 0 ? (s.spentAmount / s.receivedAmount) * 100 : 0,
        rank: idx + 1,
        trend: 'stable' as const,
      }));

      // Sort by utilization for ranking
      mappedSchools.sort((a, b) => b.overallUtilization - a.overallUtilization);
      mappedSchools.forEach((s, i) => s.rank = i + 1);

      // Process breakdown data - prefer headsBreakdownResponse if available
      const breakdownSource = headsBreakdownResponse?.success && headsBreakdownResponse.data?.breakdown
        ? headsBreakdownResponse.data.breakdown
        : [];

      // Ensure we always show all DEFAULT_HEADS (6 heads). If API breakdown misses any,
      // use defaults with zeroed values so the UI remains consistent.
      const headWiseData = (DEFAULT_HEADS || []).map((defHead, idx) => {
        const found = breakdownSource?.find((b: { head: string }) => {
          if (!b || !b.head) return false;
          const a = b.head.toString().trim().toLowerCase();
          const bname = defHead.name.toString().trim().toLowerCase();
          return a.includes(bname) || bname.includes(a);
        });

        const sanctioned = found?.sanctioned || 0;
        const spent = found?.spent || 0;
        const utilRaw = found?.utilization ?? '';
        const utilVal = typeof utilRaw === 'string' && utilRaw.includes('%')
          ? parseFloat(utilRaw.replace('%', ''))
          : (typeof utilRaw === 'number' ? utilRaw : (sanctioned > 0 ? (spent / sanctioned) * 100 : 0));

        const spentRec = found?.spentRec || 0;
        const spentNonRec = found?.spentNonRec || 0;

        return {
          headId: idx + 1,
          headCode: (defHead.code || `H${idx + 1}`) as HeadCode,
          headName: (found?.head && found.head.trim()) || defHead.name,
          color: HEAD_COLORS[(defHead.code || `H${idx + 1}`) as HeadCode],
          totalSchools: roData.schools?.length || 0,
          sanctioned,
          received: sanctioned,
          spent,
          unspent: sanctioned - spent,
          utilization: utilVal,
          targetUnits: 0,
          completedUnits: 0,
          efficiency: utilVal,
          spentRec,
          spentNonRec,
        };
      });

      const totalSpentRec = headWiseData.reduce((sum, h) => sum + h.spentRec, 0);
      const totalSpentNonRec = headWiseData.reduce((sum, h) => sum + h.spentNonRec, 0);

      // Aggregates - Prefer headsBreakdownResponse financials if available to show the exact numbers
      const totalSanctioned = headsBreakdownResponse?.success && headsBreakdownResponse.data?.financials?.sanctionedAmount !== undefined
        ? headsBreakdownResponse.data.financials.sanctionedAmount
        : mappedSchools.reduce((sum, s) => sum + s.totalSanctioned, 0);

      const totalReceived = headsBreakdownResponse?.success && headsBreakdownResponse.data?.financials?.receivedAmount !== undefined
        ? headsBreakdownResponse.data.financials.receivedAmount
        : mappedSchools.reduce((sum, s) => sum + s.totalReceived, 0);

      const totalSpent = headsBreakdownResponse?.success && headsBreakdownResponse.data?.financials?.spentAmount !== undefined
        ? headsBreakdownResponse.data.financials.spentAmount
        : mappedSchools.reduce((sum, s) => sum + s.totalSpent, 0);

      const totalUnspent = totalReceived - totalSpent;
      const overallUtilization = totalReceived > 0 ? (totalSpent / totalReceived) * 100 : 0;

      setRegion({
        roId: roData.roID,
        roCode: `RO${roData.roID}`,
        roName: roData.roName,
        totalSchools: mappedSchools.length,
        activeSchools: mappedSchools.length,
        pmShriSchools: mappedSchools.length,
        totalSanctioned,
        totalReceived,
        totalSpent,
        totalUnspent,
        overallUtilization,
        targetUnits: 0,
        completedUnits: 0,
        efficiencyPercentage: overallUtilization,
        recurringAmount: 0,
        recurringSpent: totalSpentRec,
        recurringUtilization: 0,
        nonRecurringAmount: 0,
        nonRecurringSpent: totalSpentNonRec,
        nonRecurringUtilization: 0,
        headWiseData: headWiseData,
        topPerformers: mappedSchools.slice(0, 5),
        bottomPerformers: mappedSchools.slice(-5).reverse(),
        performanceScore: overallUtilization,
        rank: 0,
        trend: 'stable',
        topPerformingSchool: mappedSchools[0]?.schoolName || '',
        lowestPerformingSchool: mappedSchools[mappedSchools.length - 1]?.schoolName || '',
        schools: mappedSchools,
        trends: [], // No trends data in this API
      });
    } else {
      // Handle empty data or reset
      setRegion(null);
    }
  }, [apiResponse, headsBreakdownResponse]);

  // Reset to page 1 when filter/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, region]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  const filteredSchools = region?.schools
    .filter(s => s.schoolName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.kvCode.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'rank') return a.rank - b.rank;
      if (sortBy === 'utilization') return b.overallUtilization - a.overallUtilization;
      return b.totalSanctioned - a.totalSanctioned;
    }) || [];



  const totalPages = Math.ceil(filteredSchools.length / itemsPerPage);
  const paginatedSchools = filteredSchools.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Convert head-wise data to Head format for chart
  const headsForChart: Head[] = region?.headWiseData.map((h) => ({
    id: h.headId,
    code: h.headCode,
    name: h.headName,
    description: '',
    color: HEAD_COLORS[h.headCode],
    totalSanctioned: h.sanctioned,
    totalReceived: h.received,
    totalSpent: h.spent,
    totalUnspent: h.unspent,
    utilizationPercentage: h.utilization,
    targetUnits: h.targetUnits,
    completedUnits: h.completedUnits,
    efficiencyPercentage: h.efficiency,
    subheadCount: 0,
  })) || [];

  const topSchools = region?.schools.slice(0, 5) || [];
  const bottomSchools = region?.schools.slice(-5).reverse() || [];

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Region Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  {region?.roName || 'Region Analytics'}
                  {region?.roCode && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                      {region.roCode}
                    </span>
                  )}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <School className="w-4 h-4" />
                    {region?.pmShriSchools || 0} PM-SHRI Schools
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Filters */}
              <div className="flex items-center gap-2">
                <YearDropdown
                  value={selectedYear}
                  onChange={(val) => setSelectedYear(val || 2025)}
                />
                <PhaseDropdown
                  value={selectedPhase}
                  onChange={setSelectedPhase}
                />
              </div>

              <UtilizationGauge value={region?.overallUtilization || 0} size="md" label="Region Utilization" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Building2 },
              { id: 'schools', label: 'Schools', icon: School },
              { id: 'heads', label: '6 Heads Analysis', icon: IndianRupee },
              // { id: 'trends', label: 'Trends', icon: TrendingUp },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${activeTab === tab.id
                  ? 'text-purple-700 border-purple-600 bg-purple-50'
                  : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <IndianRupee className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-600">Total Sanctioned</span>
                </div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(region?.totalSanctioned || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-600">Total Received</span>
                </div>
                <div className="text-2xl font-bold text-purple-600">{formatCurrency(region?.totalReceived || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-green-100">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-600">Total Spent</span>
                </div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(region?.totalSpent || 0)}</div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-orange-100">
                    <School className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm text-gray-600">PM-SHRI Schools</span>
                </div>
                <div className="text-2xl font-bold text-orange-600">{region?.pmShriSchools}</div>
              </div>
            </div>

            {/* Charts Row - Display breakdown when data is available */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Expenditure Type Breakdown</h3>
                <ExpenditureBarChart
                  data={[
                    {
                      name: 'Recurring vs Non-Recurring Spent',
                      recurring: region?.recurringSpent || 0,
                      nonRecurring: region?.nonRecurringSpent || 0,
                      recurringUtilization: region?.totalReceived ? ((region.recurringSpent) / region.totalReceived) * 100 : 0,
                      nonRecurringUtilization: region?.totalReceived ? ((region.nonRecurringSpent) / region.totalReceived) * 100 : 0,
                    },
                  ]}
                  height={250}
                  showUtilization={true}
                />
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Head-wise Utilization</h3>
                <ExpenditureBarChart
                  data={headsForChart.map(h => ({
                    name: h.code,
                    recurring: (region?.headWiseData.find(item => item.headCode === h.code) as any)?.spentRec || 0,
                    nonRecurring: (region?.headWiseData.find(item => item.headCode === h.code) as any)?.spentNonRec || 0,
                    recurringUtilization: h.utilizationPercentage,
                    nonRecurringUtilization: h.utilizationPercentage,
                  }))}
                  height={250}
                  showUtilization={false}
                />
              </div>
            </div>

            {/* Top/Bottom Schools */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-green-600" />
                  Top Performing Schools
                </h3>
                <div className="space-y-3">
                  {topSchools.map((school) => (
                    <div
                      key={school.kvId}
                      onClick={() => navigate(`/pm-shri/schools/${school.kvId}`)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    >
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-600">
                        #{school.rank}
                      </div>
                      <div className="flex-grow">
                        <div className="font-medium text-gray-900 text-sm">{school.schoolName}</div>
                        <div className="text-xs text-gray-500">{school.kvCode}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">{school.overallUtilization.toFixed(1)}%</div>
                        <div className="text-xs text-gray-500">{formatCurrency(school.totalSpent)}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  Schools Needing Attention
                </h3>
                <div className="space-y-3">
                  {bottomSchools.map((school) => (
                    <div
                      key={school.kvId}
                      onClick={() => navigate(`/pm-shri/schools/${school.kvId}`)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    >
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center font-bold text-red-600">
                        #{school.rank}
                      </div>
                      <div className="flex-grow">
                        <div className="font-medium text-gray-900 text-sm">{school.schoolName}</div>
                        <div className="text-xs text-gray-500">{school.kvCode}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-red-600">{school.overallUtilization.toFixed(1)}%</div>
                        <div className="text-xs text-gray-500">{formatCurrency(school.totalSpent)}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'heads' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Head-wise Fund Utilization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {region?.headWiseData.map((head) => (
                  <div
                    key={head.headId}
                    className="p-4 rounded-xl border border-gray-200 hover:shadow-md transition-shadow"
                    style={{ borderLeftWidth: '4px', borderLeftColor: HEAD_COLORS[head.headCode] }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: HEAD_COLORS[head.headCode] }}
                      >
                        {head.headCode}
                      </div>
                      <div className="flex-grow">
                        <div className="font-semibold text-gray-900 text-sm">{head.headName}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <span className="text-gray-500">Sanctioned:</span>
                        <div className="font-medium">{formatCurrency(head.sanctioned)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Spent:</span>
                        <div className="font-medium text-green-600">{formatCurrency(head.spent)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-grow h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${head.utilization}%`,
                            backgroundColor: HEAD_COLORS[head.headCode],
                          }}
                        />
                      </div>
                      <span className="text-sm font-bold" style={{ color: HEAD_COLORS[head.headCode] }}>
                        {head.utilization.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'schools' && (
          <div className="space-y-4">
            {/* Search & Filter */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-grow max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search schools..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sort by:</span>
                  {['rank', 'utilization', 'sanctioned'].map((sort) => (
                    <button
                      key={sort}
                      onClick={() => setSortBy(sort as typeof sortBy)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sortBy === sort
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {sort.charAt(0).toUpperCase() + sort.slice(1)}
                    </button>
                  ))}
                </div>
                <span className="ml-auto text-sm text-gray-500">
                  {filteredSchools.length} schools
                </span>
              </div>
            </div>

            {/* Schools Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">School</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Sanctioned</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Received</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Recurring</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Non-Recurring</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Spent</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Utilization</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedSchools.map((school) => (
                      <tr key={school.kvId} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${school.rank <= 5 ? 'bg-green-100 text-green-600' :
                              school.rank <= 10 ? 'bg-blue-100 text-blue-600' :
                                school.rank <= 20 ? 'bg-yellow-100 text-yellow-600' :
                                  'bg-gray-100 text-gray-600'
                              }`}>
                              {school.rank}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{school.schoolName}</div>
                          <div className="text-xs text-gray-500">{school.kvCode}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(school.totalSanctioned)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">
                          {formatCurrency(school.totalReceived)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(school.recurringAmount)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(school.nonRecurringAmount)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          {formatCurrency(school.totalSpent)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${school.overallUtilization >= 75 ? 'bg-green-500' :
                                  school.overallUtilization >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                style={{ width: `${Math.min(school.overallUtilization, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{school.overallUtilization.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => navigate(`/pm-shri/schools/${school.kvId}`)}
                            className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredSchools.length)}</span> of{' '}
                  <span className="font-medium">{filteredSchools.length}</span> schools
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Simple pagination logic to always show a few pages
                        let pageNum = i + 1;
                        if (totalPages > 5 && currentPage > 3) {
                          pageNum = currentPage - 3 + i;
                          // stick to end
                          if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === pageNum
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 italic">
              Trend data not available for this view.
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default RegionAnalyticsScreen;
